import React, { useEffect, useState } from "react";
import { FiUsers, FiCalendar, FiFolder, FiArrowRight } from "react-icons/fi";
import { Card, Badge } from "../ui";
import { getEvents, getProjects } from "../../api/communityContent";

export default function OverviewTab({ community, onNavigateToTab }) {
  const [upcomingEvent, setUpcomingEvent] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    setLoadingPreview(true);
    // Small, targeted previews — not the full events/projects lists, which
    // only load when their own tabs are actually opened. Overview just
    // needs enough to be a real landing view instead of one paragraph of
    // description text.
    Promise.allSettled([
      getEvents(community.id, { limit: 1, upcoming: true }),
      getProjects(community.id, { limit: 3 }),
    ]).then(([eventsRes, projectsRes]) => {
      if (eventsRes.status === "fulfilled") {
        setUpcomingEvent(eventsRes.value.data?.events?.[0] || null);
      }
      if (projectsRes.status === "fulfilled") {
        setRecentProjects(projectsRes.value.data?.projects || []);
      }
      setLoadingPreview(false);
    });
  }, [community.id]);

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-slate-900">About this community</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{community.description || "No description added yet."}</p>
        <div className="mt-4 flex items-center gap-2 border-t border-slate-50 pt-4">
          <Badge variant="slate">
            <FiUsers className="h-3 w-3" /> {community.memberCount ?? 0} member{community.memberCount === 1 ? "" : "s"}
          </Badge>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <FiCalendar className="h-4 w-4 text-primary-500" /> Upcoming event
            </h3>
            <button onClick={() => onNavigateToTab("events")} className="text-xs font-medium text-primary-600 hover:text-primary-700">
              View all
            </button>
          </div>
          {loadingPreview ? (
            <div className="h-12 animate-pulse rounded-lg bg-slate-100" />
          ) : upcomingEvent ? (
            <div>
              <p className="text-sm font-medium text-slate-800">{upcomingEvent.title}</p>
              <p className="mt-1 text-xs text-slate-500">
                {new Date(upcomingEvent.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                {upcomingEvent.location ? ` · ${upcomingEvent.location}` : ""}
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No upcoming events scheduled.</p>
          )}
        </Card>

        <Card className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <FiFolder className="h-4 w-4 text-primary-500" /> Recent projects
            </h3>
            <button onClick={() => onNavigateToTab("projects")} className="text-xs font-medium text-primary-600 hover:text-primary-700">
              View all
            </button>
          </div>
          {loadingPreview ? (
            <div className="space-y-2">
              <div className="h-4 animate-pulse rounded bg-slate-100" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
            </div>
          ) : recentProjects.length === 0 ? (
            <p className="text-sm text-slate-400">No projects shared yet.</p>
          ) : (
            <ul className="space-y-2">
              {recentProjects.map((p) => (
                <li key={p.id} className="truncate text-sm text-slate-700">
                  {p.title}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <button
        onClick={() => onNavigateToTab("members")}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-200 py-2.5 text-sm font-medium text-slate-500 hover:border-primary-200 hover:text-primary-600"
      >
        See all {community.memberCount ?? 0} members <FiArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
